import { useState, useEffect } from 'react';
import { zonasAPI, geocodeAPI } from '../api/client';
import { useTranslation } from 'react-i18next';

export default function Zonas() {
  const { t } = useTranslation();
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => zonasAPI.list().then(r => setZonas(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>{t('zonas.title')}</h1>
          <p style={S.sub}>{t('zonas.subtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal({ mode: 'create' })}>{t('zonas.newBtn')}</button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 60, color: '#8B6355' }}>{t('common.loading')}</div>
        : zonas.length === 0 ? (
          <div className="empty-state">
            <div className="icon">📍</div>
            <h3>{t('zonas.emptyTitle')}</h3>
            <p>{t('zonas.emptyMsg')}</p>
            <button className="btn btn-primary" onClick={() => setModal({ mode: 'create' })}>{t('zonas.createFirstBtn')}</button>
          </div>
        ) : (
          <div style={S.grid}>
            {zonas.map(z => (
              <div key={z.id} className="card card-body">
                <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                  <span style={{ fontSize: 24 }}>📍</span>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: 17, fontFamily: "'DM Sans', sans-serif" }}>{z.nombre}{z.altitud != null && <span style={{ fontSize: 13, fontWeight: 400, color: '#8B6355' }}> · {Math.round(z.altitud)} m</span>}</h3>
                    {z.descripcion && <p style={{ fontSize: 13, color: '#8B6355', marginTop: 4 }}>{z.descripcion}</p>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <span className="badge badge-amber">🐝 {t('zonas.hiveCount', { count: z.colmenas_count, s: z.colmenas_count !== 1 ? 's' : '' })}</span>
                  {z.lat != null && z.lng != null && <span className="badge badge-green">🌍 GPS</span>}
                </div>
                {z.lat != null && z.lng != null && <div style={{ fontSize: 11, color: '#8B6355', fontFamily: 'monospace', marginBottom: 12 }}>{z.lat.toFixed(4)}, {z.lng.toFixed(4)}</div>}
                <div style={{ display: 'flex', gap: 8, borderTop: '1px solid #F0EDE9', paddingTop: 12 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setModal({ mode: 'edit', zona: z })}>{t('common.edit')}</button>
                  <button className="btn btn-sm" style={{ color: '#C0392B', background: '#FDECEA', border: 'none' }} onClick={() => setDeleting(z)}>{t('common.delete')}</button>
                </div>
              </div>
            ))}
          </div>
        )}

      {modal && <ZonaModal mode={modal.mode} zona={modal.zona}
        onSave={async (data) => { modal.mode === 'create' ? await zonasAPI.create(data) : await zonasAPI.update(modal.zona.id, data); setModal(null); load(); }}
        onClose={() => setModal(null)} />}

      {deleting && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDeleting(null)}>
          <div className="modal">
            <div className="modal-header"><h2>{t('zonas.deleteTitle')}</h2></div>
            <div className="modal-body">
              <p>{t('zonas.deleteConfirm', { name: deleting.nombre })}</p>
              <p style={{ color: '#C0392B', fontSize: 13, marginTop: 8 }}>{t('zonas.deleteWarning')}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleting(null)}>{t('common.cancel')}</button>
              <button className="btn btn-danger" onClick={async () => { await zonasAPI.delete(deleting.id); setDeleting(null); load(); }}>{t('common.yes')}, {t('common.delete').toLowerCase()}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ZonaModal({ mode, zona, onSave, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ nombre: zona?.nombre || '', descripcion: zona?.descripcion || '' });
  const [coords, setCoords] = useState(zona?.lat != null && zona?.lng != null ? { lat: zona.lat, lng: zona.lng } : null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsErr, setGpsErr] = useState('');
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({ lat: '', lng: '' });

  const submit = async e => {
    e.preventDefault(); setErr(''); setSaving(true);
    try {
      await onSave({ ...form, lat: coords ? coords.lat : null, lng: coords ? coords.lng : null });
    } catch (ex) { setErr(ex.response?.data?.detail || t('common.error')); }
    finally { setSaving(false); }
  };

  const useGPS = () => {
    setGpsErr('');
    if (!navigator.geolocation) { setGpsErr(t('zonas.gpsNotSupported')); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setCoords({ lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6) }); setGpsLoading(false); },
      () => { setGpsErr(t('zonas.gpsDenied')); setGpsLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const search = async () => {
    if (!query.trim() || searching) return;
    setSearching(true); setResults(null);
    try {
      const r = await geocodeAPI.search(query.trim());
      setResults(r.data);
    } catch { setResults([]); }
    finally { setSearching(false); }
  };

  const applyManual = () => {
    const la = parseFloat(manual.lat), lo = parseFloat(manual.lng);
    if (isNaN(la) || isNaN(lo) || la < -90 || la > 90 || lo < -180 || lo > 180) { setGpsErr(t('zonas.manualInvalid')); return; }
    setGpsErr('');
    setCoords({ lat: la, lng: lo });
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{mode === 'create' ? t('zonas.modalCreate') : t('zonas.modalEdit')}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">{t('zonas.nameLabel')}</label>
              <input name="nombre" value={form.nombre} onChange={e => setForm(f => ({...f, nombre: e.target.value}))} className="form-input" placeholder={t('zonas.namePlaceholder')} required />
            </div>
            <div className="form-group">
              <label className="form-label">{t('zonas.descLabel')}</label>
              <textarea name="descripcion" value={form.descripcion} onChange={e => setForm(f => ({...f, descripcion: e.target.value}))} className="form-input" placeholder={t('zonas.descPlaceholder')} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('zonas.locationLabel')}</label>
              {coords ? (
                <div style={{ background: '#F0F7EE', border: '1px solid #CBE3C4', borderRadius: 8, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13 }}>📍 {t('zonas.locationSet')}: <span style={{ fontFamily: 'monospace' }}>{coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}</span></span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setCoords(null); setResults(null); setQuery(''); }}>{t('zonas.changeLocation')}</button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button type="button" className="btn btn-secondary" onClick={useGPS} disabled={gpsLoading}>
                    {gpsLoading ? t('zonas.gpsLoading') : `📍 ${t('zonas.gpsBtn')}`}
                  </button>
                  <div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input value={query} onChange={e => setQuery(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); search(); } }}
                        className="form-input" placeholder={`🔍 ${t('zonas.searchPlaceholder')}`} style={{ flex: 1 }} />
                      <button type="button" className="btn btn-secondary" onClick={search} disabled={searching || !query.trim()}>
                        {searching ? '...' : t('zonas.searchBtn')}
                      </button>
                    </div>
                    {results !== null && (
                      results.length === 0
                        ? <div style={{ fontSize: 13, color: '#8B6355', marginTop: 8 }}>{t('zonas.searchNoResults')}</div>
                        : <div style={{ marginTop: 8, border: '1px solid #F0EDE9', borderRadius: 8, overflow: 'hidden' }}>
                            {results.map((r, i) => (
                              <button key={i} type="button" onClick={() => { setCoords({ lat: r.lat, lng: r.lng }); setResults(null); }}
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', background: 'white', border: 'none', borderTop: i > 0 ? '1px solid #F0EDE9' : 'none', cursor: 'pointer', fontSize: 13 }}>
                                📍 {r.display_name}
                              </button>
                            ))}
                          </div>
                    )}
                  </div>
                  <div>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowManual(s => !s)}>
                      ⚙️ {t('zonas.manualToggle')} {showManual ? '▲' : '▼'}
                    </button>
                    {showManual && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <label className="form-label">{t('zonas.latLabel')}</label>
                            <input type="number" step="any" value={manual.lat} onChange={e => setManual(m => ({...m, lat: e.target.value}))} className="form-input" placeholder="46.9835" />
                          </div>
                          <div>
                            <label className="form-label">{t('zonas.lngLabel')}</label>
                            <input type="number" step="any" value={manual.lng} onChange={e => setManual(m => ({...m, lng: e.target.value}))} className="form-input" placeholder="9.5732" />
                          </div>
                        </div>
                        <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={applyManual} disabled={manual.lat === '' || manual.lng === ''}>
                          {t('zonas.manualApply')}
                        </button>
                      </div>
                    )}
                  </div>
                  {gpsErr && <div style={{ background: '#FFF6E5', color: '#8B6355', padding: 10, borderRadius: 8, fontSize: 13 }}>{gpsErr}</div>}
                </div>
              )}
            </div>
            {err && <div style={{ background: '#FDECEA', color: '#C0392B', padding: 10, borderRadius: 8, marginTop: 12, fontSize: 13 }}>{err}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? t('common.saving') : mode === 'create' ? t('zonas.createFirstBtn') : t('common.save')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const S = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title: { fontSize: 26, fontFamily: "'DM Sans', sans-serif" },
  sub: { color: '#8B6355', fontSize: 14, marginTop: 4 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 },
};
